import React from 'react';
import { LucideIcon } from 'lucide-react';

/**
 * P2-07. Chip was 11 variants × 4 sizes plus a `customColor` escape hatch and
 * five exported config maps. Three of the variants (`neutral`, `secondary` and —
 * after P1-02 — `primary`) were byte-identical, and `xs`/`sm` were both `text-xs`
 * differing by 2px of horizontal padding. Eleven names, six actual looks.
 *
 * The axes are now orthogonal and meaningful:
 *   tone     — what the chip MEANS
 *   emphasis — how loudly it says it
 *   size     — sm in tables and dense rows, md standing alone
 *
 * `customColor` is gone. It was the mechanism by which the drift entered, and no
 * call site passed it — every use was inside the config maps described below.
 */

export type ChipTone = 'neutral' | 'info' | 'positive' | 'caution' | 'negative' | 'brand';
export type ChipEmphasis = 'soft' | 'solid';
export type ChipSize = 'sm' | 'md';

/** The pre-P2-07 names. Still accepted, mapped below; prefer `tone`. */
export type LegacyChipVariant =
  | 'success' | 'warning' | 'danger' | 'info' | 'neutral'
  | 'primary' | 'accent' | 'purple' | 'custom' | 'secondary' | 'outline';

interface ChipProps {
  label: string;
  tone?: ChipTone;
  emphasis?: ChipEmphasis;
  size?: ChipSize | 'xs' | 'lg';
  /**
   * @deprecated Use `tone`. Kept because 76 files pass this — 79 of those
   * dynamically, from `getStatusVariant`/`getBudgetStatusVariant` — so a hard
   * rename would have been a mass edit with no visual payoff. New code should
   * not use it.
   */
  variant?: LegacyChipVariant;
  className?: string;
  onClick?: () => void;
  icon?: LucideIcon;
}

const TONES: Record<ChipTone, Record<ChipEmphasis, string>> = {
  neutral: {
    soft: 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary border border-border-light dark:border-border-dark',
    solid: 'bg-slate-700 text-white border border-slate-700',
  },
  info: {
    soft: 'bg-slate-100 dark:bg-slate-900/20 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800',
    solid: 'bg-slate-600 text-white border border-slate-600',
  },
  positive: {
    soft: 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-300 border border-success-200 dark:border-success-800',
    solid: 'bg-success-700 text-white border border-success-700',
  },
  caution: {
    soft: 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300 border border-warning-200 dark:border-warning-800',
    solid: 'bg-warning-700 text-white border border-warning-700',
  },
  negative: {
    soft: 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-300 border border-error-200 dark:border-error-800',
    solid: 'bg-error-600 text-white border border-error-600',
  },
  brand: {
    // P1-02: was bg-primary-100 / border-primary-200 — i.e. gray on gray, which
    // made "Passed" render identically to "Monitor". The tint is now a token
    // (primary.tint / primary.tint-border) because positionColors needs the same
    // pair; see the note in tailwind.config.js for why these exact values.
    soft: 'bg-primary-tint dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-tint-border dark:border-primary-800',
    solid: 'bg-primary-700 text-white border border-primary-700',
  },
};

/*
  `solid` fills at the 600/700 step, never 500. White on the 500s fails AA badly
  at chip text sizes — success-500 measures 2.28:1 and error-500 3.74:1 — and the
  deleted POSITION_CONFIGS used exactly those for StrSupport and StrOppose. The
  600/700 steps clear 4.5:1 (success-700 5.02, warning-700 5.05, error-600 4.83,
  slate-600 7.58, primary-700 10.9).

  Nothing passes emphasis="solid" yet. It exists so the next chip that needs to
  shout is not hand-rolled at 2.28:1 the way those were.
*/

/** Old variant names → the axes. Three of them already shared one look. */
const LEGACY_TONE: Record<LegacyChipVariant, ChipTone> = {
  success: 'positive',
  warning: 'caution',
  danger: 'negative',
  info: 'info',
  accent: 'info',
  neutral: 'neutral',
  secondary: 'neutral',
  outline: 'neutral',
  custom: 'neutral',
  primary: 'brand',
  purple: 'brand',
};

const SIZES: Record<ChipSize, string> = {
  sm: 'px-2.5 py-0.5 text-xs',
  md: 'px-3 py-1.5 text-sm',
};

/** xs and lg were never distinct enough to keep: xs differed from sm by 2px. */
const LEGACY_SIZE: Record<'xs' | 'lg', ChipSize> = { xs: 'sm', lg: 'md' };

const Chip: React.FC<ChipProps> = ({
  label,
  tone,
  emphasis = 'soft',
  size = 'md',
  variant,
  className = '',
  onClick,
  icon: Icon,
}) => {
  const resolvedTone: ChipTone = tone ?? (variant ? LEGACY_TONE[variant] : 'neutral');
  const resolvedSize: ChipSize = size === 'xs' || size === 'lg' ? LEGACY_SIZE[size] : size;

  /** w-fit: avoid stretching to full width in flex column parents; max-w-full: allow shrinking in narrow cells */
  const baseClasses =
    'font-medium rounded-full inline-flex w-fit max-w-full items-center justify-center transition-all duration-200';
  const toneClasses = TONES[resolvedTone][emphasis];

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClasses} ${toneClasses} ${SIZES[resolvedSize]} cursor-pointer hover:opacity-80 hover:scale-105 ${className}`}
      >
        {Icon && <Icon className="w-3 h-3 mr-1" aria-hidden="true" />}
        {label}
      </button>
    );
  }

  /*
    A static chip is a label, not a live region. It used to carry role="status",
    which makes every chip an aria-live region — a 50-row table announced 50 of
    them, and none was a status update anyone needed read aloud. The label text
    is already part of the accessible name of whatever contains it.
  */
  return (
    <span className={`${baseClasses} ${toneClasses} ${SIZES[resolvedSize]} ${className}`}>
      {Icon && <Icon className="w-3 h-3 mr-1" aria-hidden="true" />}
      {label}
    </span>
  );
};

// Determine a chip variant from a bill status string.
export const getStatusVariant = (status: string): LegacyChipVariant => {
  if (!status) return 'neutral';
  const statusLower = status.toLowerCase();

  // Signed/Passed laws
  if (statusLower.includes('signed') || statusLower.includes('became law')) {
    return 'success';
  }
  // Bills that passed readings
  else if (statusLower.includes('passed') || statusLower.includes('third reading')) {
    return 'primary'; // brand purple for passed bills
  }
  // Committee/In Progress
  else if (statusLower.includes('committee') || statusLower.includes('introduced') || statusLower.includes('laid over')) {
    return 'warning';
  }
  // Failed/Postpone Indefinitely
  else if (statusLower.includes('died') || statusLower.includes('postponed') || statusLower.includes('failed')) {
    return 'danger';
  }
  // Information/Amendments
  else if (statusLower.includes('amendment') || statusLower.includes('no amendments')) {
    return 'info';
  }

  return 'neutral';
};

// Budget item status variant mapping
export const getBudgetStatusVariant = (status: string): LegacyChipVariant => {
  if (!status) return 'neutral';
  const s = status.toLowerCase();

  if (s.includes('approved') || s.includes('enacted')) return 'success';
  if (s.includes('flagged') || s.includes('flag')) return 'warning';
  if (s.includes('rejected') || s.includes('cut') || s.includes('eliminated')) return 'danger';
  if (s.includes('under review') || s.includes('in progress') || s.includes('active')) return 'info';
  if (s.includes('draft') || s.includes('proposed')) return 'primary';

  return 'neutral';
};

/*
  Deleted here: POSITION_CONFIGS, BUDGET_POSITION_CONFIGS, PRIORITY_CONFIGS,
  DEPARTMENT_CONFIGS and getChamberVariant.

  The design review said to keep the first three and repoint them at the new
  axes. They have no production consumers — the only file importing any of them
  was this component's own test — so repointing would have meant maintaining an
  API nothing calls, and would have carried their sub-AA solid fills forward.

  DEPARTMENT_CONFIGS was the clearest case: Technology / Design / Marketing /
  Sales / Legal are not concepts in a legislative product, and it was the app's
  only source of pink and orange chips.

  For whoever needs position chips next: CandidateTable.tsx renders them from its
  own local getPositionColorClasses and maps Monitor to amber, where
  POSITION_CONFIGS mapped it to neutral. That divergence predates this change and
  is left alone rather than silently repainted — but it is exactly what a shared
  position config should fix, once one is actually used.
*/

export default Chip;
